import React, { useEffect, useState } from "react";
import {
  Grid,
  Container,
  Card,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getAccessToken, useAuth } from "../../context/auth";
import Header, { HeaderType } from "../../components/Header";
import { ViewDetail, DeleteForm } from "../../services/api/accident-service";
import type {
  IDocument,
  IQuestionAnswer,
} from "../../services/model/accidentSupportRequest";
import AdjustSharpIcon from "@mui/icons-material/AdjustSharp";
import { useConfirm } from "@/components/dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";

interface GenericViewFormProps {
  style?: React.CSSProperties;
  loading: boolean;
}

type ViewQA = IQuestionAnswer & {
  raw?: unknown;
  mediaImages?: Array<{ document: string }>;
  mediaRecording?: string;
};

type ViewDoc = {
  document?: string;
  file?: string;
  imageBase64?: string;
};

const looksLikeImageBase64 = (s: string) =>
  /^[A-Za-z0-9+/=]+$/.test(s) && s.length > 120;

const asRecord = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === "object" ? (v as Record<string, unknown>) : null;

// Normalize a value for <img src="..."> (supports raw base64 or full data URLs)
const asImageDataUrl = (v?: string | null) => {
  const s = (v ?? "").trim();
  if (!s) return "";
  if (s.startsWith("data:image/")) return s;
  if (/^https?:\/\//i.test(s) || s.startsWith("blob:")) return s;
  if (looksLikeImageBase64(s)) return `data:image/jpeg;base64,${s}`;
  return "";
};

const parseJsonMaybe = (input: unknown): unknown => {
  if (typeof input !== "string") return input;
  const trimmed = input.trim();
  if (!trimmed) return input;
  if (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  ) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return input;
    }
  }
  return input;
};

const asMaybeDoc = (value: unknown): string => {
  const s = String(value ?? "").trim();
  if (!s) return "";
  if (s.startsWith("data:image/")) return s;
  if (/^https?:\/\//i.test(s) || s.startsWith("blob:")) return s;
  if (looksLikeImageBase64(s)) return s;
  return "";
};

const normalizeImages = (row: Record<string, unknown>): Array<{ document: string }> => {
  const answerParsed = parseJsonMaybe(row?.answer);
  const parsedObj = asRecord(answerParsed);
  const candidates = [
    row?.images,
    row?.documents,
    row?.mediaImages,
    row?.image,
    row?.document,
    parsedObj?.images,
    parsedObj?.documents,
    parsedObj?.mediaImages,
    parsedObj?.image,
    parsedObj?.document,
    answerParsed,
  ];

  const toArray = (v: unknown): unknown[] => {
    if (!v) return [];
    if (Array.isArray(v)) return v;
    if (typeof v === "string") {
      const trimmed = v.trim();
      if (!trimmed) return [];
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        const doc = asMaybeDoc(v);
        return doc ? [{ document: doc }] : [];
      }
    }
    if (typeof v === "object") return [v];
    return [];
  };

  const flattened = candidates.flatMap((c) => toArray(c));
  return flattened
    .map((d) => {
      const docObj = asRecord(d);
      const direct =
        docObj?.document ??
        docObj?.file ??
        docObj?.imageBase64 ??
        docObj?.base64 ??
        docObj?.url ??
        docObj?.uri ??
        d;
      return { document: asMaybeDoc(direct) };
    })
    .filter((d) => d.document.trim().length > 0);
};

const asAudioSrc = (v?: string | null) => {
  const s = (v ?? "").trim();
  if (!s) return "";
  if (s.startsWith("data:audio/")) return s;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("blob:")) return s;
  if (/^[A-Za-z0-9+/=]+$/.test(s) && s.length > 120) {
    return `data:audio/wav;base64,${s}`;
  }
  return "";
};

const getRecordingValue = (row: Record<string, unknown>): string => {
  const direct = String(row?.recording ?? "").trim();
  if (direct) return direct;

  const parsed = parseJsonMaybe(row?.answer);
  const parsedObj = asRecord(parsed);
  if (parsedObj) {
    const nested = String(
      parsedObj?.recording ??
        parsedObj?.audio ??
        parsedObj?.voice ??
        "",
    ).trim();
    if (nested) return nested;
  }

  const answer = String(row?.answer ?? "").trim();
  return asAudioSrc(answer) ? answer : "";
};

const getAddressText = (raw: unknown) => {
  if (typeof raw !== "string") return String(raw ?? "");
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    try {
      const parsed = JSON.parse(trimmed);
      const parsedObj = asRecord(parsed);
      return String(
        parsedObj?.googleAddress ??
          parsedObj?.address ??
          parsedObj?.formattedAddress ??
          raw,
      );
    } catch {
      return raw;
    }
  }
  return raw;
};

const GenericViewForm: React.FC<GenericViewFormProps> = () => {
  const navigate = useNavigate();
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";
  const confirm = useConfirm();

  const [qa, setQA] = useState<ViewQA[]>([]);
  const [loading, setLoading] = useState(false);

  const cancel = () => {
    navigate(-1);
  };

  const runDelete = async () => {
    try {
      setLoading(true);
      const accidentId = JSON.parse(
        window.localStorage.getItem("accidentId") || ""
      );
      const formTypeID = JSON.parse(
        window.localStorage.getItem("formTypeId") || ""
      );
      const listId = JSON.parse(window.localStorage.getItem("ListId") || "");
      await DeleteForm(token, accidentId, formTypeID, listId);
      setLoading(false);
      cancel();
    } catch (err) {
      setLoading(false);
      showDialog(
        "Failure",
        "An exception occurred, please contact your administrator."
      );
      if (err instanceof Error) {
        console.error(err.message);
      }
    }
  };

  const deleteRecord = () => {
    confirm({
      catchOnCancel: false,
      title: "Confirmation",
      description: "Are you sure you would like to delete this record?",
    }).then(() => runDelete());
  };

  const showDialog = (title: string, message: string) => {
    confirm({
      catchOnCancel: false,
      title: title,
      description: message,
    });
  };

  const fetchQuestion = async () => {
    try {
      setLoading(true);
      const accidentId = JSON.parse(
        window.localStorage.getItem("accidentId") || ""
      );
      const formTypeID = JSON.parse(
        window.localStorage.getItem("formTypeId") || ""
      );
      const listId = JSON.parse(window.localStorage.getItem("ListId") || "");

      const response = await ViewDetail(token, accidentId, formTypeID, listId);

      if (response) {
        const responseData = (response as { data?: unknown })?.data;
        const nestedData =
          responseData && typeof responseData === "object"
            ? (responseData as { data?: unknown }).data
            : undefined;
        const rows: unknown[] = Array.isArray(nestedData)
          ? nestedData
          : Array.isArray(responseData)
            ? responseData
            : [];

        // Build a clean, dense array (no holes)
        const qaArray: ViewQA[] = rows.map((rowRaw) => {
          const row = asRecord(rowRaw) ?? {};
          const mediaImages = normalizeImages(row);
          const mediaRecording = getRecordingValue(row);
          const item: ViewQA = {
            id: row["id"] as string,
            questionId: row["questionId"] as string,
            question: row["question"] as string,
            answer: row["answer"] as string,
            listId: row["listId"] as string,
            formTitle: row["formTitle"] as string,
            questionType: row["questionType"] as string,
            images: (mediaImages as unknown as IDocument[]) ?? [],
            recording: mediaRecording || (row["recording"] as string) || "",
            mediaImages,
            mediaRecording,
            raw: row,
          };
          return item;
        });

        setQA(qaArray);
        setLoading(false);
      } else {
        setLoading(false);
        showDialog(
          "Failure",
          "There seems to be a problem with your internet connection.\nPlease check your internet connection."
        );
      }
    } catch (err) {
      setLoading(false);
      showDialog(
        "Failure",
        "An exception occurred, please contact your administrator."
      );
      if (err instanceof Error) {
        console.error(err.message);
      }
    }
  };

  useEffect(() => {
    fetchQuestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function GetControl(
    answer: ViewQA,
    index: string | number,
  ): React.ReactNode {
    const qType = String(answer.questionType ?? "").toLowerCase().trim();

    const docs: ViewDoc[] = (answer.mediaImages ?? []).filter(Boolean);
    const answerAsImage = asImageDataUrl(answer?.answer ?? "");

    if (
      qType.includes("camera") ||
      docs.length > 0 ||
      !!answerAsImage
    ) {
      const finalDocs: ViewDoc[] =
        docs.length > 0 ? docs : answerAsImage ? [{ document: answerAsImage }] : [];
      if (!finalDocs.length) return null;

      return (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          direction="column"
          spacing={1}
        >
          {finalDocs.map((docItem, i) => {
            const src = asImageDataUrl(
              docItem?.document ?? docItem?.file ?? docItem?.imageBase64 ?? "",
            );
            if (!src) return null;
            return (
              <Grid key={`${answer.questionId}-img-${i}`}>
                <img
                  alt="doc-img"
                  src={src}
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    borderRadius: 8,
                    display: "block",
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      );
    } else if (qType.includes("voice")) {
      const src = asAudioSrc(
        answer.mediaRecording || answer.recording || answer.answer || "",
      );
      if (!src) return null;
      return (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          direction="column"
        >
          <audio src={src} controls style={{ width: "100%" }} />
        </Grid>
      );
    } else if (qType.includes("vehicle")) {
      const val = String(answer?.answer ?? "");
      const short = val.length < 15;
      return (
        <TextField
          key={index}
          disabled
          variant="outlined"
          fullWidth
          multiline={!short}
          maxRows={10}
          value={val}
        />
      );
    } else if (qType.includes("address")) {
      const val = getAddressText(answer?.answer ?? "");
      const short = val.length < 15;
      return (
        <TextField
          key={index}
          disabled
          variant="outlined"
          fullWidth
          multiline={!short}
          maxRows={10}
          value={val}
        />
      );
    } else {
      const val = String(answer?.answer ?? "");
      const short = val.length < 15;
      return (
        <TextField
          key={index}
          disabled
          variant="outlined"
          fullWidth
          multiline={!short}
          maxRows={10}
          value={val}
        />
      );
    }
  }

  return (
    <div>
      <Header type={HeaderType.AccidentSupportAdd} />
      <Container maxWidth="sm">
        <Grid container spacing={1}>
          <Grid size={12}>
            <Card variant="outlined" style={{ padding: "15px" }}>
              <Grid
                container
                direction="row"
                justifyContent="center"
                spacing={1}
              >
                <Grid size={12}>
                  <Card variant="outlined" style={{ padding: "15px" }}>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      direction="column"
                    >
                      <Typography variant="subtitle1">
                        <strong> Detail</strong>
                      </Typography>
                    </Grid>
                  </Card>

                  <Grid size={12}>
                    <Card variant="outlined" style={{ padding: "15px" }}>
                      {qa.map((record, index) => (
                        <div key={`${record.questionId}-${index}`}>
                          <Button
                            startIcon={<AdjustSharpIcon />}
                            fullWidth
                            size="large"
                            sx={{ justifyContent: "flex-start" }}
                          >
                            <Typography variant="subtitle2">
                              <strong>{record.question}</strong>
                            </Typography>
                          </Button>
                          {GetControl(record, record.questionId + index)}
                          <br />
                          <br />
                        </div>
                      ))}
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              <br />

              <Grid size={12}>
                <br />
                <Button
                  onClick={deleteRecord}
                  value="Delete"
                  variant="contained"
                  color="secondary"
                  fullWidth
                  size="large"
                >
                  <strong>Delete</strong>
                </Button>
              </Grid>

              <Grid size={12}>
                <br />
                <Button
                  onClick={cancel}
                  value="cancel"
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  size="large"
                >
                  <strong>Back</strong>
                </Button>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Container>
      <BrandLoader open={loading} />
    </div>
  );
};

export default GenericViewForm;
